// DatePillPopup.jsx
import React, { useState } from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";
import HierarchicalDatePicker from "./HierarchicalDatePicker";
import "../styles/DatePillPopup.css";

/**
 * Create a Date at start of day in local time
 */
function createLocalDate(year, month, day) {
  const date = new Date(year, month, day);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * Format date as "DD MMM YYYY" in local time
 */
function formatLocalDate(date) {
  if (!date) return "Select Date";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function DatePillPopup({ date, onChange, minDate = null, maxDate = null }) {
  const [isOpen, setIsOpen] = useState(false);

  // Ensure dates are at start of day
  const normalizedDate = date ? createLocalDate(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  ) : null;

  const safeMinDate = minDate ? createLocalDate(
    minDate.getFullYear(),
    minDate.getMonth(),
    minDate.getDate()
  ) : createLocalDate(2022, 0, 1);

  const safeMaxDate = maxDate ? createLocalDate(
    maxDate.getFullYear(),
    maxDate.getMonth(),
    maxDate.getDate()
  ) : createLocalDate(2025, 11, 31);

  const displayLabel = formatLocalDate(normalizedDate);

  const handleToggle = () => {
    if (!validateDates(safeMinDate, safeMaxDate)) return;
    setIsOpen((prev) => !prev);
  };

  const handleDateSelected = (newDate) => {
    // Ensure new date is at start of day
    const normalizedNewDate = createLocalDate(
      newDate.getFullYear(),
      newDate.getMonth(),
      newDate.getDate()
    );
    onChange(normalizedNewDate);
    setIsOpen(false);
  };

  function validateDates(minD, maxD) {
    if (!(minD instanceof Date) || isNaN(minD.getTime())) {
      console.error("Invalid minDate:", minD);
      return false;
    }
    if (!(maxD instanceof Date) || isNaN(maxD.getTime())) {
      console.error("Invalid maxDate:", maxD);
      return false;
    }
    if (minD > maxD) {
      console.error("minDate cannot be greater than maxDate");
      return false;
    }
    return true;
  }

  const portalRoot = document.getElementById("date-picker-portal");
  const popupContent = (
    <div className="popup-content">
      <HierarchicalDatePicker
        selectedDate={normalizedDate}
        onChange={handleDateSelected}
        minDate={safeMinDate}
        maxDate={safeMaxDate}
        onClose={() => setIsOpen(false)}
      />
    </div>
  );

  return (
    <div className="date-pill-popup">
      <button type="button" className="date-pill-button" onClick={handleToggle}>
        {displayLabel}
      </button>
      {isOpen && portalRoot
        ? ReactDOM.createPortal(popupContent, portalRoot)
        : null}
    </div>
  );
}

DatePillPopup.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  onChange: PropTypes.func.isRequired,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
};

export default DatePillPopup;
